import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import SideBar from '../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../hooks/usePageLayout';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { useApiPageAccess } from '../../../hooks/useApiPageAccess';
import ApiKeyManagement from './ApiKeyManagement';
import MetaTitle from '../../utils/MetaTitle';
import {
  gray40,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  red50,
  tertiaryButtonBackgroundColor,
  white,
  white_overlay80,
} from '../../../constants/colors';
import { boldXxl, regularMd, regularSm, spHeaderTitle } from '../../../constants/location';

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  buttonLarge: {
    height: '44px',
    padding: '0 16px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 32px 32px',
    minHeight: '560px',
    padding: '24px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: '0 0 24px',
      padding: '16px',
      width: '100%',
    },
  },
  messageWrapper: {
    margin: '24px 32px',
    padding: '24px',
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    '@media screen and (max-width:960px)': {
      margin: '16px',
      padding: '16px',
    },
  },
  messageText: {
    ...regularMd,
    color: gray100,
  },
  errorText: {
    ...regularSm,
    color: red50,
  },
  notAvailableLabel: {
    ...regularSm,
    color: gray80,
    marginBottom: '8px',
  },
};

const ApiKeyManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { loading, errorText, apiEnabled, canAccess, startDateText, effectiveCompanyId } =
    useApiPageAccess();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="APIキー管理" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box sx={layoutStyles.header}>
          <Typography sx={styles.headerTitle}>APIキー管理</Typography>
          {canAccess && (
            <Box sx={styles.headerActions}>
              <Button
                onClick={(): void => navigate('/api-docs')}
                startIcon={<MenuBookIcon sx={{ height: '18px', width: '18px' }} />}
                sx={[tertiaryButtonBackgroundColor, styles.buttonLarge]}
              >
                API ドキュメントを開く
              </Button>
              <Button
                onClick={(): void => setCreateOpen(true)}
                startIcon={<AddIcon sx={{ height: '18px', width: '18px' }} />}
                sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
              >
                APIキーを発行
              </Button>
            </Box>
          )}
        </Box>

        {loading ? (
          <CustomLoading loading />
        ) : errorText ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.errorText}>{errorText}</Typography>
          </Box>
        ) : !apiEnabled ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.notAvailableLabel}>API 機能 未契約</Typography>
            <Typography sx={styles.messageText}>
              この企業では API
              機能が有効化されていません。利用を希望する場合は事務局までお問い合わせください。
            </Typography>
          </Box>
        ) : !canAccess ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.notAvailableLabel}>利用開始日まで待機中</Typography>
            <Typography sx={styles.messageText}>
              API 機能の利用開始日（{startDateText}
              ）にまだ到達していません。開始日以降にアクセスしてください。
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.tableSection}>
            <ApiKeyManagement
              companyId={effectiveCompanyId ?? undefined}
              createOpen={createOpen}
              onCreateClose={(): void => setCreateOpen(false)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ApiKeyManagementPage;
